import userSession from '@webapp-next/lib/user-session';
import {get} from '@webapp-next/lib/request/webapp/apiv2';
// Do NOT remove. This is for unit testing purposes
import * as thisModule from './people-edit-pay-detail-item.js';
import { toRaw } from 'vue';
import useEmploymentStore from '@/modules/people/store/employment';
import useRootStore from '@/modules/people/store/root';
import userHasPermission from '@/lib/user-has-permission';
import componentDisabledCheck from '@webapp-next/lib/component-disabled-check';
import { isPayrollIdValidationFlagOn } from '@/modules/people/store/uk-payroll-gate';
import { checkPaySdpGeneral } from '@webapp-next/lib/check-pay-sensitive-data-permissions';
import {
  getPayrollIdErrorKind,
  isPayrollIdServerMessage,
} from '@/modules/people/utils/payroll-id-validation';
import usePeopleStore from '@/modules/people/store';
import { normalisePayrollId } from '@/modules/people/utils/payroll-id-validation';
import { checkEmploymentCollapseFF, isAboveCollapseThreshold } from '@/modules/people/utils/people-feature-flags';

export const ERROR_TYPES_ENUM = {
  PAY_CENTER: 'paycenter',
  PAY_PERIOD: 'payperiod',
  PAY_LOCATION: 'paylocation',
  PAYROLL_ID: 'payrollid',
};


/**
 * Structures locations so that they can be used by the multi-selector component
 *
 * @param {Object[]} locations
 * @param {Object} loggedUserAccessibleLocations
 * @return {Object[]}
 */
export function sanitizeLocations(locations, loggedUserAccessibleLocations) {
  if (!locations) {
    return [];
  }

  return locations.map(item => {
    return {
      id: item.Id,
      link: item.Id,
      label: item.CompanyName,
      active: item.Active,
      noRemovePermission: !loggedUserAccessibleLocations[item.Id],
      canSelect: !!loggedUserAccessibleLocations[item.Id],
      parentId: 0,
      children: [],
    };
  });
}

/**
 * Structures workDetail locations so that they can be used by the multi-selector component.
 * Unlike sanitizeLocations, sanitizeWorkDetailLocations assumes each location
 *  already has the noRemovePermission and canSelect properties.
 *
 * @param {Object[]} locations
 * @return {Object[]}
 */
export function sanitizeWorkDetailLocations(locations) {
  if (!locations) {
    return [];
  }

  return locations.map(item => {
    return {
      id: item.link,
      link: item.link,
      label: item.label,
      active: item.active,
      noRemovePermission: item.noRemovePermission,
      canSelect: item.canSelect,
      parentId: 0,
      children: [],
    };
  });
}

/**
 * Gets only locations accessible to the current user
 * @return {Object<string, boolean>}
 */
export function getLoggedUserAccessibleLocations() {
  return (userSession().MySetup.Workplace || []).reduce((reduced, { Id }) => {
    reduced[Id] = true;
    return reduced;
  }, {});
}

/**
 * Takes an array of contracts and an object of contractsIndexedByID
 * and inserts all the contracts into contractsIndexedByID.
 * Will add any new contracts from the array into the ID-indexed object.
 * Will also overwrite any existing contracts in the ID-indexed object.
 *
 * @param {Object[]} contracts
 * @param {Object} contractsIndexedByID
 * @return {Object}
 */
export function createContractsIndexedByIDObject(
  contracts = [],
  contractsIndexedByID = {}
) {
  const rootState = useRootStore();
  contracts.forEach(contract => {
    // Sync the term array from EmploymentTerms
    let matchedEmploymentTerm =
      rootState.employmentTermIndexedByFileName[contract.Code] || {};
    if (!contract.Id) {
      if (
        matchedEmploymentTerm &&
        !matchedEmploymentTerm.AlreadySyncedContracts
      ) {
        matchedEmploymentTerm.TermId = matchedEmploymentTerm.Id;
        matchedEmploymentTerm.Id = String(matchedEmploymentTerm.FileName);
        matchedEmploymentTerm.AlreadySyncedContracts = true;
      }
    } else {
      matchedEmploymentTerm = {};
    }

    // TODO: Remove the forced PeriodType
    const newContract = {
      ...contract,
      ...matchedEmploymentTerm,
    };
    contractsIndexedByID[newContract.Id] = newContract;
  });
  return contractsIndexedByID;
}

/**
 * Will fetch only contracts that it hasn't already fetched before
 * including contracts that have been modified.
 *
 * @return {Object}
 */
export const getOnlyNewOrUpdatedContracts = (function() {
  return async function() {
    return await thisModule.getContracts();
  };
})();

/**
 * Gets all the employment contracts.
 * The earliestModifiedTime parameter is used to fetch only the contracts that
 * have a 'modified' time that is more recent than the earliestModifiedTime provided.
 * @return {Promise<Object[]>}
 */
export async function getContracts() {
  const { data } = await get('api/v2/people/contracts');
  return data.contracts;
}

/**
 * Validate the pay period and pay center if it has been used in another employee pay detail.
 * @param {Object} payDetail
 *  @param {number} payDetail.payPeriod
 *  @param {number} payDetail.payCentre
 * @param {number} payDetailIndex - the index of which pay detail is being validated. -1 means it is a new pay detail
 * @param {boolean} payCentreOnly
 * @return {Object}
 */
export function validatePayPeriodPayCentre(
  payDetail,
  payDetailIndex,
  payCentreOnly = false
) {
  if ((!payDetail.payPeriod && !payCentreOnly) || !payDetail.payCentre) {
    return {};
  }

  const employmentState = useEmploymentStore();
  const peopleState = usePeopleStore();
  const { payPeriod, payCentre } = payDetail;

  function isDuplicate(pair) {
    return payCentreOnly
      ? pair.payCentre === payCentre && payCentre
      : pair.payPeriod === payPeriod &&
          pair.payCentre === payCentre &&
          payPeriod &&
          payCentre;
  }

  const allPairs = employmentState.allAgreementPairs;
  const agreementCount = (peopleState.employee?.Agreements || []).length;
  const isCollapseEnabled = checkEmploymentCollapseFF() && isAboveCollapseThreshold(agreementCount) && allPairs.length;

  // When the collapse FF is off, or the pay detail is unsaved (agreementId is 0/falsy),
  // use index-based exclusion against localPayDetail. Unsaved pay details all share
  // agreementId 0, so agreementId-based exclusion cannot distinguish between them.
  if (!isCollapseEnabled || !payDetail.agreementId) {
    const localMatch = employmentState.localPayDetail
      .filter((pd, index) => index !== payDetailIndex && isDuplicate(pd));

    if (localMatch[0]) {
      return localMatch[0];
    }

    // For unsaved pay details, also check persisted allAgreementPairs to catch
    // duplicates against inactive agreements not yet loaded into localPayDetail.
    if (isCollapseEnabled && payDetail.agreementId === 0) {
      return allPairs.filter(isDuplicate)[0];
    }

    return undefined;
  }

  // Merge persisted pairs with local unsaved edits (local wins by agreementId)
  const pairsByAgreementId = new Map(
    allPairs.map(pair => [pair.agreementId, pair])
  );
  employmentState.localPayDetail.forEach(pd => {
    if (pd.agreementId !== undefined && pd.agreementId !== null) {
      pairsByAgreementId.set(pd.agreementId, {
        payCentre: pd.payCentre,
        payPeriod: pd.payPeriod,
        agreementId: pd.agreementId,
        active: pd.active,
      });
    }
  });

  // Exclude current agreement by ID
  const currentAgreementId = payDetail.agreementId;
  return [...pairsByAgreementId.values()]
    .filter(pair => pair.agreementId !== currentAgreementId && isDuplicate(pair))[0];
}

/**
 * Assigns validation type and message on the pay detail object
 * @param {Object} error
 * @param {string} error.type
 * @param {string} error.value
 * @param {Object} payDetail
 */
export function setValidationError(error, payDetail) {
  const { type, value } = error;
  payDetail.validationError[type] = value;
}

/**
 * Removes one validation error from a pay detail object and leaves the others alone
 * @param {string} type
 * @param {Object} payDetail
 */
export function clearValidationErrorByType(type, payDetail) {
  if (payDetail?.validationError && payDetail.validationError[type]) {
    delete payDetail.validationError[type];
  }
}

/**
 * Gives the pay detail a plain-object validationError. Enterprise pay details loaded from the
 * server start with an array there, and in Vue 3 adding a named key to an array does not notify
 * the computeds that read Object.values() of it (canSave, canSubmit), so the save would stay
 * enabled. Replacing it also makes watchers on the validationError reference run again.
 * @param {Object} payDetail
 */
function ensureValidationErrorObject(payDetail) {
  if (Array.isArray(payDetail.validationError)) {
    payDetail.validationError = {};
  }
}

// The pay details that have a Payroll ID editor on screen, keyed by the raw pay detail object and
// counting the editors. A server refusal is placed only on a pay detail found here.
const mountedPayrollIdEditors = new WeakMap();

/**
 * Records that an editor showing this pay detail's Payroll ID field is mounted. Call it when the
 * editor mounts and call the returned function when it unmounts.
 * @param {Object} payDetail
 * @return {function(): void} removes the record
 */
export function registerPayrollIdEditor(payDetail) {
  const key = payDetail ? toRaw(payDetail) : null;
  if (!key) {
    return () => {};
  }
  mountedPayrollIdEditors.set(key, (mountedPayrollIdEditors.get(key) || 0) + 1);
  let removed = false;
  return () => {
    if (removed) {
      return;
    }
    removed = true;
    const count = (mountedPayrollIdEditors.get(key) || 0) - 1;
    if (count > 0) {
      mountedPayrollIdEditors.set(key, count);
    } else {
      mountedPayrollIdEditors.delete(key);
    }
  };
}

function hasMountedPayrollIdEditor(payDetail) {
  return mountedPayrollIdEditors.has(toRaw(payDetail));
}

/**
 * The Payroll ID the server holds for a pay detail: the saved PayrollId of its agreement (in
 * enterprise mode each pay detail carries its own agreement's code). When no agreement record is
 * found, the employee's saved Payroll ID is used, as the single pay detail path does. A pay detail
 * with no agreement yet has nothing saved, so its baseline is ''. The pay detail itself cannot be
 * the baseline, because a modal clone may already carry a value typed earlier and not saved.
 * @param {Object} payDetail
 * @return {string}
 */
export function getSavedPayrollId(payDetail) {
  if (!payDetail?.agreementId) {
    return '';
  }
  const peopleState = usePeopleStore();
  const agreement = peopleState.getEmployeeAgreementsById(payDetail.agreementId);
  return normalisePayrollId(
    agreement ? agreement.PayrollId : peopleState.employee?.PayrollId
  );
}

/**
 * Removes the PAYROLL_ID error and any remembered server refusal from a pay detail.
 * @param {Object} payDetail
 * @return {void}
 */
export function clearPayrollIdState(payDetail) {
  if (!payDetail) {
    return;
  }
  if (payDetail.validationError) {
    clearValidationErrorByType(ERROR_TYPES_ENUM.PAYROLL_ID, payDetail);
  }
  payDetail.payrollIdServerErrorValue = undefined;
  payDetail.payrollIdServerErrorMessage = undefined;
}

/**
 * Sets or clears the PAYROLL_ID error on a pay detail for the client-side rules.
 * When the gate is false the client rules are not applied (no change for other tenants).
 * A server refusal (see applyPayrollIdServerError) stays until the value is edited, whatever
 * the gate says: the server only refuses a Payroll ID on a UK payroll tenant, so its answer is
 * authoritative even when the status request has not settled or failed. Other code replaces
 * validationError with a fresh object (clearValidationError on a pay centre or pay period
 * change), so the refusal is put back from payrollIdServerErrorMessage while the trimmed value
 * still equals the refused one.
 * @param {Object} payDetail
 * @param {boolean} isUkPayrollGate - true only for a UK payroll tenant and an editable field
 * An inactive (archived) pay detail is not judged: the backend skips it, so its error is cleared.
 * When storedPayrollId (the saved Payroll ID, see getSavedPayrollId) is given and the
 * trimmed current value still equals it, only the "required" rule applies: the backend judges
 * a Payroll ID only when it changed, so a legacy stored code that is too long or has invalid
 * characters must not block saving an unrelated edit.
 * @param {function(string): string} t - called with 'payroll-id-' plus the error kind
 * @param {string|null} [storedPayrollId] - undefined keeps the rules applying to any value
 * @return {void}
 */
export function validatePayrollId(payDetail, isUkPayrollGate, t, storedPayrollId) {
  if (!payDetail?.validationError) {
    return;
  }
  if (payDetail.active === false) {
    clearPayrollIdState(payDetail);
    return;
  }
  const serverValue = payDetail.payrollIdServerErrorValue;
  const serverMessage = payDetail.payrollIdServerErrorMessage;
  if (
    serverValue !== undefined &&
    serverMessage &&
    serverValue === normalisePayrollId(payDetail.payrollId)
  ) {
    if (payDetail.validationError[ERROR_TYPES_ENUM.PAYROLL_ID] !== serverMessage) {
      ensureValidationErrorObject(payDetail);
      setValidationError(
        { type: ERROR_TYPES_ENUM.PAYROLL_ID, value: serverMessage },
        payDetail
      );
    }
    return;
  }
  if (serverValue !== undefined || serverMessage !== undefined) {
    payDetail.payrollIdServerErrorValue = undefined;
    payDetail.payrollIdServerErrorMessage = undefined;
  }
  let kind = isUkPayrollGate ? getPayrollIdErrorKind(payDetail.payrollId) : null;
  const currentValue = normalisePayrollId(payDetail.payrollId);
  if (
    kind &&
    kind !== 'required' &&
    storedPayrollId !== undefined &&
    currentValue !== '' &&
    currentValue === normalisePayrollId(storedPayrollId)
  ) {
    kind = null;
  }
  if (kind) {
    ensureValidationErrorObject(payDetail);
    setValidationError(
      { type: ERROR_TYPES_ENUM.PAYROLL_ID, value: t(`payroll-id-${kind}`) },
      payDetail
    );
  } else {
    clearValidationErrorByType(ERROR_TYPES_ENUM.PAYROLL_ID, payDetail);
  }
}

/**
 * Shows a backend Payroll ID refusal against the Payroll ID field, but only when it is certain
 * which pay detail it belongs to: when there is exactly one active pay detail (archived ones are
 * not counted). With several, the message does not say which one, and
 * employmentState.editPayDetail cannot be trusted (it aliases whichever pay detail was last
 * touched, and closing the modal never clears it), so the message is not placed and the toast
 * alone shows it. The message is also not placed when no editor for that pay detail is mounted
 * (the card view, for example): the error could not be seen there, yet it would block Save.
 *
 * The server's answer is taken as it is, without consulting the UK payroll gate: the backend
 * refuses a Payroll ID only on a UK payroll tenant, so a save sent before the gate's status
 * request settled (or after it failed) still lands its error on the field.
 *
 * The message is also not placed when the Payroll ID flag is off (see uk-payroll-gate.ts) or an
 * embed locks the Payroll ID field.
 *
 * The match is on the English prefix "Payroll ID". The backend wraps the message in _sp(), so in
 * another locale the match fails safely: nothing is placed and only the toast shows.
 * @param {string} message
 * @return {boolean} true when the message was placed on a field
 */
export function applyPayrollIdServerError(message) {
  if (!isPayrollIdValidationFlagOn() || !isPayrollIdServerMessage(message)) {
    return false;
  }
  // When pay details are hidden or not manageable the field is not rendered, so an error placed
  // on it could never be cleared and would lock Save.
  const paySdp = checkPaySdpGeneral();
  if (
    !userHasPermission('Can_Manage_Employee_PayDetails') ||
    !paySdp.hasAccess
  ) {
    return false;
  }
  // An embed that locks the field leaves the user no way to correct it, so the error would
  // block Save for good. The toast alone shows the refusal.
  if (componentDisabledCheck('editPeopleEmploymentForm')('PayrollId', true)) {
    return false;
  }
  const employmentState = useEmploymentStore();
  const activePayDetails = (employmentState.localPayDetail || []).filter(
    payDetail => payDetail?.active
  );
  if (activePayDetails.length !== 1) {
    return false;
  }
  const target = activePayDetails[0];
  if (!target?.validationError || !hasMountedPayrollIdEditor(target)) {
    return false;
  }
  ensureValidationErrorObject(target);
  setValidationError({ type: ERROR_TYPES_ENUM.PAYROLL_ID, value: message }, target);
  target.payrollIdServerErrorValue = normalisePayrollId(target.payrollId);
  target.payrollIdServerErrorMessage = message;
  return true;
}

/**
 * Clears validationError of a pay detail object
 * @param {Object} payDetail
 */
export function clearValidationError(payDetail) {
  payDetail.validationError = {};
}

/**
 * Check if the select level should be visible
 * @param {Object} contractLevels
 * @return {Boolean}
 */
export function checkSelectLevelVisible(contractLevels) {
  return contractLevels?.length > 0;
}

/**
 * Initialise level for pay detail
 * @param {Object} payDetail
 * @param {Object[]} levels
 */
export function setPayDetailLevel(payDetail, level) {
  payDetail.level = level;
  // When level is cleared (contract change), mark the picked-for-contract
  // marker as null — a distinct sentinel from undefined. The library
  // pay-rules child remounts on contract change (parent keys by
  // `${type}-${contract}`) and seeds undefined back to the current
  // contract; using null instead means the seed condition is skipped and
  // the gate re-fires for the new contract (including the A→B→A path).
  if (level === '' || level === null || level === undefined) {
    payDetail.levelPickedForContract = null;
  }
}
